import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Database, KeyRound, Loader2, Save, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { errMsg } from "../ui";
import { fmtDayY } from "./meta";

function Toggle({ checked, onChange, title, hint, testid, disabled = false }) {
  return (
    <label className={`flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2 ${disabled ? "opacity-60" : ""}`}>
      <span><span className="block text-xs font-medium text-slate-700">{title}</span><span className="block text-[11px] text-slate-500">{hint}</span></span>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testid} />
    </label>
  );
}

function WebcdrCard({ w }) {
  return (
    <Card className="flex items-start gap-3 border-slate-200 px-5 py-4 shadow-sm" data-testid="uc-settings-webcdr">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-50 text-sky-600"><KeyRound className="h-4 w-4" /></div>
      <div className="min-w-0 text-xs text-slate-600">
        <h3 className="font-heading text-sm font-semibold text-slate-800">WebCDR login</h3>
        {w.configured ? <p>Signed in as <b>{w.username}</b> · <span className="break-all font-mono text-[11px]">{w.base_url}</span></p>
          : <p className="text-amber-700">Not set up yet.</p>}
        <p className="mt-1 text-[11px] text-slate-500">Kept in <Link to="/settings?tab=api-connections" className="font-medium text-emerald-700 underline" data-testid="uc-settings-webcdr-link">Settings → API Connections</Link> under the alias <span className="font-mono">webcdr</span>.</p>
      </div>
    </Card>
  );
}

function JumpCard({ f, setF }) {
  return (
    <Card className="overflow-hidden border-slate-200 shadow-sm" data-testid="uc-settings-jumps">
      <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-rose-50 text-rose-600"><TrendingUp className="h-4 w-4" /></div>
        <div><h3 className="font-heading text-sm font-semibold text-slate-800">Unusual changes</h3>
          <p className="text-xs text-slate-500">Each domain's cycle amount is compared with the previous cycle's full total. New domains with charges are flagged too. Applies from the next run.</p></div>
      </div>
      <div className="flex flex-wrap gap-6 px-5 py-4 text-xs text-slate-600">
        <label className="flex items-center gap-2">Flag a change of
          <Input type="number" min={5} max={1000} value={f.jump_pct} onChange={(e) => setF({ ...f, jump_pct: e.target.value })} className="h-9 w-20 font-mono" data-testid="uc-settings-jump-pct" />% or more, up or down</label>
        <label className="flex items-center gap-2">Ignore domains under $
          <Input type="number" min={0} step="0.01" value={f.jump_min} onChange={(e) => setF({ ...f, jump_min: e.target.value })} className="h-9 w-24 font-mono" data-testid="uc-settings-jump-min" />in both cycles</label>
      </div>
    </Card>
  );
}

/** Usage Charges → Settings: the CDR table, the daily schedule and its summary e-mail. */
export function UsageSettings({ ov, reload }) {
  const c = ov.config;
  const [f, setF] = useState({ connection_id: c.connection_id, table: c.table, daily_enabled: c.daily_enabled, email_summary: c.email_summary,
    jump_pct: c.jump_pct, jump_min: c.jump_min, daily_writes: c.daily_writes });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put("/usage-charges/config", { ...f, jump_pct: Number(f.jump_pct), jump_min: Number(f.jump_min) }); toast.success("Usage Charges settings saved"); reload(); }
    catch (e) { toast.error(errMsg(e, "Could not save")); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-3xl space-y-4" data-testid="uc-settings">
      <Card className="overflow-hidden border-slate-200 shadow-sm">
        <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600"><Database className="h-4 w-4" /></div>
          <div><h3 className="font-heading text-sm font-semibold text-slate-800">Where the calls are saved</h3>
            <p className="text-xs text-slate-500">The billing database and the CDR table the old get.php script filled. The table is checked when you save.</p></div>
        </div>
        <div className="space-y-4 px-5 py-4">
          <label className="block space-y-1"><span className="text-xs font-medium text-slate-600">Connection</span>
            <select value={f.connection_id} onChange={(e) => setF({ ...f, connection_id: e.target.value })} className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="uc-settings-connection">
              <option value="">Pick a MySQL connection…</option>
              {ov.connections.map((x) => <option key={x.id} value={x.id}>{`${x.name} — ${x.host}/${x.database}`}</option>)}</select></label>
          <label className="block space-y-1"><span className="text-xs font-medium text-slate-600">CDR table</span>
            <Input value={f.table} onChange={(e) => setF({ ...f, table: e.target.value })} className="h-9 w-72 font-mono text-sm" data-testid="uc-settings-table" /></label>
        </div>
      </Card>
      <Card className="overflow-hidden border-slate-200 shadow-sm">
        <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600"><CalendarClock className="h-4 w-4" /></div>
          <div><h3 className="font-heading text-sm font-semibold text-slate-800">Daily schedule</h3>
            <p className="text-xs text-slate-500">Every day at 6:00 AM Eastern: collect yesterday and the day before (UTC), then total the open cycle through the latest day with calls saved and check Stax Bill. {!ov.config.stax_live ? "Stax Bill stays a dry run until a super admin switches updates on." : f.daily_writes ? "Stax Bill updates are ON and the daily run writes them." : "Stax Bill updates are ON for runs started by hand; the daily run stays a dry run."} Missed days are caught up, up to a week back. Last day collected by the schedule: <b data-testid="uc-settings-last-day">{c.last_daily_day ? fmtDayY(c.last_daily_day) : "none yet"}</b>.</p></div>
        </div>
        <div className="space-y-3 px-5 py-4">
          <Toggle checked={f.daily_enabled} onChange={(v) => setF({ ...f, daily_enabled: v })} title="Run every day" hint="Off: nothing is collected unless someone starts a run by hand." testid="uc-settings-daily" />
          <Toggle checked={f.daily_writes} onChange={(v) => setF({ ...f, daily_writes: v })} disabled={!ov.is_super} title="Daily run writes to Stax Bill"
            hint={`${ov.is_super ? "" : "Super admins only. "}On: the daily run writes the amounts once Stax Bill updates are on. Off: it stays a dry run and only runs started by hand write.`} testid="uc-settings-daily-writes" />
          <Toggle checked={f.email_summary} onChange={(v) => setF({ ...f, email_summary: v })} title="E-mail a summary after each daily run" hint="Goes to the billing alert recipients (Billing Sync → Settings), flagged when anything failed. Every domain attached as CSV." testid="uc-settings-email" />
        </div>
      </Card>
      <JumpCard f={f} setF={setF} />
      <div className="flex justify-end"><Button onClick={save} disabled={busy || !f.connection_id || !f.table.trim()} data-testid="uc-settings-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Check & save</Button></div>
      <WebcdrCard w={ov.webcdr} />
    </div>
  );
}
